import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuizPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    goal: '',
    fitness_level: '',
    body_type: '',
    age: '',
    current_weight: '',
    target_weight: '',
    equipment: [],
    diet_preference: '',
    restrictions: []
  });

  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId') || `user_${Date.now()}`;
      localStorage.setItem('userId', userId);

      const response = await axios.post(`${BACKEND_URL}/api/quiz/submit`, {
        user_id: userId,
        goal: formData.goal,
        fitness_level: formData.fitness_level,
        body_type: formData.body_type,
        age: parseInt(formData.age),
        current_weight: parseFloat(formData.current_weight),
        target_weight: parseFloat(formData.target_weight),
        equipment: formData.equipment,
        diet_preference: formData.diet_preference,
        restrictions: formData.restrictions
      });

      toast.success('Dein personalisierter Plan wird erstellt!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Quiz submission error:', error);
      toast.error('Fehler beim Erstellen deines Plans');
    } finally {
      setLoading(false);
    }
  };

  const equipmentOptions = ['Keine', 'Hanteln', 'Widerstandsbänder', 'Kettlebell', 'Yoga-Matte', 'Fitnessstudio'];
  const restrictionsOptions = ['Keine', 'Vegetarisch', 'Vegan', 'Glutenfrei', 'Laktosefrei', 'Nussallergie'];

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-zinc-400">Schritt {step} von {totalSteps}</span>
            <span className="text-sm text-purple-400 font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </motion.div>

        {/* Quiz Card */}
        <motion.div 
          className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-8 shadow-2xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Goal */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Was ist dein Hauptziel?</h2>
                <RadioGroup value={formData.goal} onValueChange={(value) => setFormData({ ...formData, goal: value })}>
                  {['Gewicht verlieren', 'Muskeln aufbauen', 'Fit bleiben', 'Tonen & Formen'].map((option) => (
                    <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors cursor-pointer">
                      <RadioGroupItem value={option} id={option} />
                      <Label htmlFor={option} className="cursor-pointer flex-1">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </motion.div>
            )}

            {/* Step 2: Fitness Level */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Wie würdest du dein Fitness-Level beschreiben?</h2>
                <RadioGroup value={formData.fitness_level} onValueChange={(value) => setFormData({ ...formData, fitness_level: value })}>
                  {['Anfänger', 'Fortgeschritten', 'Profi'].map((option) => (
                    <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors cursor-pointer">
                      <RadioGroupItem value={option} id={option} />
                      <Label htmlFor={option} className="cursor-pointer flex-1">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </motion.div>
            )}

            {/* Step 3: Body Type & Age */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Erzähl uns mehr über dich</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="body_type">Körpertyp</Label>
                    <RadioGroup value={formData.body_type} onValueChange={(value) => setFormData({ ...formData, body_type: value })} className="mt-2">
                      {['Ektomorph (schlank)', 'Mesomorph (athletisch)', 'Endomorph (kräftig)'].map((option) => (
                        <div key={option} className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors cursor-pointer">
                          <RadioGroupItem value={option} id={option} />
                          <Label htmlFor={option} className="cursor-pointer flex-1 text-sm">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="age">Alter</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="z.B. 28"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="mt-2 h-12 bg-zinc-900/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 rounded-lg text-white"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Weight Goals */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Deine Gewichtsziele</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current_weight">Aktuelles Gewicht (kg)</Label>
                    <Input
                      id="current_weight"
                      type="number"
                      step="0.1"
                      placeholder="z.B. 65"
                      value={formData.current_weight}
                      onChange={(e) => setFormData({ ...formData, current_weight: e.target.value })}
                      className="mt-2 h-12 bg-zinc-900/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="target_weight">Zielgewicht (kg)</Label>
                    <Input
                      id="target_weight"
                      type="number"
                      step="0.1"
                      placeholder="z.B. 58"
                      value={formData.target_weight}
                      onChange={(e) => setFormData({ ...formData, target_weight: e.target.value })}
                      className="mt-2 h-12 bg-zinc-900/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 rounded-lg text-white"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 5: Equipment */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Welche Ausrüstung hast du?</h2>
                <p className="text-zinc-400">Wähle alle, die zutreffen</p>
                <div className="space-y-3">
                  {equipmentOptions.map((option) => (
                    <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors">
                      <Checkbox
                        id={option}
                        checked={formData.equipment.includes(option)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, equipment: [...formData.equipment, option] });
                          } else {
                            setFormData({ ...formData, equipment: formData.equipment.filter(e => e !== option) });
                          }
                        }}
                      />
                      <Label htmlFor={option} className="cursor-pointer flex-1">{option}</Label>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 6: Diet */}
            {step === 6 && (
              <motion.div
                key="step6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-semibold">Ernährungspräferenzen</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Ernährungsstil</Label>
                    <RadioGroup value={formData.diet_preference} onValueChange={(value) => setFormData({ ...formData, diet_preference: value })} className="mt-2">
                      {['Alles', 'Vegetarisch', 'Vegan', 'Low-Carb', 'High-Protein'].map((option) => (
                        <div key={option} className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors cursor-pointer">
                          <RadioGroupItem value={option} id={option} />
                          <Label htmlFor={option} className="cursor-pointer flex-1 text-sm">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label>Einschränkungen oder Allergien</Label>
                    <div className="space-y-2 mt-2">
                      {restrictionsOptions.map((option) => (
                        <div key={option} className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-800 hover:border-purple-500/50 transition-colors">
                          <Checkbox
                            id={`restriction-${option}`}
                            checked={formData.restrictions.includes(option)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, restrictions: [...formData.restrictions, option] });
                              } else {
                                setFormData({ ...formData, restrictions: formData.restrictions.filter(r => r !== option) });
                              }
                            }}
                          />
                          <Label htmlFor={`restriction-${option}`} className="cursor-pointer flex-1 text-sm">{option}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-zinc-800">
            <Button
              data-testid="quiz-back-btn"
              onClick={handleBack}
              disabled={step === 1 || loading}
              variant="ghost"
              className="h-10 px-4 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Zurück
            </Button>

            {step < totalSteps ? (
              <Button
                data-testid="quiz-next-btn"
                onClick={handleNext}
                className="h-12 px-8 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold"
              >
                Weiter
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="quiz-submit-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="h-12 px-8 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Erstelle Plan...
                  </>
                ) : (
                  'Plan erstellen'
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QuizPage;